import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
Container,
Box,
Typography,
Card,
CardContent,
CardHeader,
Divider,
Grid,
Paper,
Tabs,
Tab,
Button,
Avatar,
Chip,
useMediaQuery,
useTheme,
Slide,
Grow,
Fade,
CircularProgress,
List,
ListItem,
ListItemIcon,
ListItemText,
AvatarGroup,
Stack,
IconButton,
Tooltip,
LinearProgress,
Accordion,
AccordionSummary,
AccordionDetails
} from '@mui/material';
import {
Person as PersonIcon,
Title as TitleIcon,
Description as DescriptionIcon,
AttachMoney as AttachMoneyIcon,
Groups as GroupsIcon,
ArrowBack as ArrowBackIcon,
School as SchoolIcon,
CalendarToday as CalendarIcon,
Schedule as ScheduleIcon,
Business as BusinessIcon,
TrendingUp as TrendingUpIcon,
GroupWork as GroupWorkIcon,
AccountCircle as AccountCircleIcon,
CorporateFare as CorporateFareIcon,
Launch as LaunchIcon,
Share as ShareIcon,
Bookmark as BookmarkIcon,
Download as DownloadIcon,
ExpandMore as ExpandMoreIcon,
TrackChanges as TrackChangesIcon,
Assessment as AssessmentIcon,
Public as PublicIcon,
Science as ScienceIcon,
CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { APIResearcherList, APIUniversitiesList } from '../../utilities/APIS';
import { daysPassed, getIdCategory, getIdFullName } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';

const ProjectDetails = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
const navigate = useNavigate();
const location = useLocation();

const [state, setState] = useState({
    ResearchersData: [],
    UniversitiesData: [],
    activeTab: 0,
    loading: true,
    bookmarked: false,
    expandedSections: {}
});

const {
    researcherName,
    uploadDate,
    projectEnd,
    projectStart,
    universityName,
    projectTitle,
    projectFunders,
    projectDescription,
    projectMembers,
    coreInvestigator,
    objectives,
    projectActivities,
    proposedBudget,
    expectedOutcomes,
    evaluationPlan,
    sustainabilityPlan
} = location.state || {};

useEffect(() => {
    loadData();
}, []);

const loadData = async () => {
    try {
    const [researchersRes, universitiesRes] = await Promise.all([
        axios.get(APIResearcherList, { headers }),
        axios.get(APIUniversitiesList, { headers })
    ]);

    setState(prev => ({
        ...prev,
        ResearchersData: researchersRes.data.data || [],
        UniversitiesData: universitiesRes.data.data || [],
        loading: false
    }));
    } catch (err) {
    console.log(err);
    setState(prev => ({ ...prev, loading: false }));
    }
};

const handleTabChange = (event, newValue) => {
    setState(prev => ({ ...prev, activeTab: newValue }));
};

const handleSectionExpand = (section) => {
    setState(prev => ({
        ...prev,
        expandedSections: {
            ...prev.expandedSections,
            [section]: !prev.expandedSections[section]
        }
    }));
};

const getProjectStatus = () => {
    const currentDate = new Date();
    const startDate = new Date(projectStart);
    const endDate = new Date(projectEnd);
    
    if (currentDate < startDate) {
    return { status: 'Upcoming', color: 'info', progress: 0 };
    } else if (currentDate > endDate) {
    return { status: 'Completed', color: 'success', progress: 100 };
    } else {
    const totalDuration = endDate - startDate;
    const elapsed = currentDate - startDate;
    const progress = Math.min((elapsed / totalDuration) * 100, 100);
    return { status: 'In Progress', color: 'warning', progress };
    }
};

const handleBookmark = () => {
    setState(prev => ({ ...prev, bookmarked: !prev.bookmarked }));
};

const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
};

const hasAdditionalDetails = () => {
    return coreInvestigator || 
           objectives || 
           projectActivities || 
           proposedBudget || 
           expectedOutcomes || 
           evaluationPlan || 
           sustainabilityPlan;
};

const renderTabContent = () => {
    switch (state.activeTab) {
    case 0:
        return renderOverviewTab();
    case 1:
        return renderDescriptionTab();
    case 2:
        return renderFundersTab();
    case 3:
        return renderMembersTab();
    default:
        return null;
    }
};

const renderOverviewTab = () => (
    <Box sx={{ p: 3 }}>
    <Grid container spacing={3}>
        {/* Project Status Card */}
        <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
            <CardHeader
            title="Project Timeline"
            avatar={<CalendarIcon color="primary" />}
            />
            <CardContent>
            <Stack spacing={2}>
                <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status
                </Typography>
                <Chip 
                    label={getProjectStatus().status} 
                    color={getProjectStatus().color}
                    variant="filled"
                />
                </Box>
                
                <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Progress
                </Typography>
                <LinearProgress 
                    variant="determinate" 
                    value={getProjectStatus().progress} 
                    sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {Math.round(getProjectStatus().progress)}% Complete
                </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                    Start Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                    {projectStart}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                    End Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                    {projectEnd}
                    </Typography>
                </Box>
                </Box>
            </Stack>
            </CardContent>
        </Card>
        </Grid>

        {/* Principal Investigator Card */}
        <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
            <CardHeader
            title="Principal Investigator"
            avatar={<PersonIcon color="primary" />}
            />
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                <AccountCircleIcon />
                </Avatar>
                <Box>
                <Typography variant="h6" fontWeight="bold">
                    {getIdFullName(researcherName, state.ResearchersData)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Lead Researcher
                </Typography>
                </Box>
            </Box>
            
            {coreInvestigator && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <ScienceIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">
                    <strong>Core Investigator:</strong> {coreInvestigator}
                </Typography>
                </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">
                {getIdCategory(universityName, state.UniversitiesData)}
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">
                Uploaded {daysPassed(uploadDate)} days ago
                </Typography>
            </Box>
            </CardContent>
        </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2 }}>
            <CardHeader
            title="Project Overview"
            avatar={<TrendingUpIcon color="primary" />}
            />
            <CardContent>
            <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                    <GroupWorkIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="primary">
                    {projectMembers ? JSON.parse(projectMembers).length : 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    Team Members
                    </Typography>
                </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                    <CorporateFareIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="secondary">
                    {projectFunders ? JSON.parse(projectFunders).length : 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    Funding Partners
                    </Typography>
                </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                    {Math.ceil((new Date(projectEnd) - new Date(projectStart)) / (1000 * 60 * 60 * 24))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    Project Days
                    </Typography>
                </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                    <BusinessIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                    1
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                    University
                    </Typography>
                </Box>
                </Grid>
            </Grid>
            </CardContent>
        </Card>
        </Grid>

        {/* Detailed Planning Section - Only shown if there are additional details */}
        {hasAdditionalDetails() && (
            <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
                <CardHeader
                title="Detailed Project Planning"
                avatar={<ScienceIcon color="primary" />}
                />
                <CardContent>
                    {coreInvestigator && (
                        <Accordion 
                            expanded={state.expandedSections.coreInvestigator || false}
                            onChange={() => handleSectionExpand('coreInvestigator')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Core Investigator
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1">{coreInvestigator}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {objectives && (
                        <Accordion 
                            expanded={state.expandedSections.objectives || false}
                            onChange={() => handleSectionExpand('objectives')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <TrackChangesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Project Objectives
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{objectives}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {projectActivities && (
                        <Accordion 
                            expanded={state.expandedSections.activities || false}
                            onChange={() => handleSectionExpand('activities')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Project Activities
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{projectActivities}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {proposedBudget && (
                        <Accordion 
                            expanded={state.expandedSections.budget || false}
                            onChange={() => handleSectionExpand('budget')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Proposed Budget
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{proposedBudget}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {expectedOutcomes && (
                        <Accordion 
                            expanded={state.expandedSections.outcomes || false}
                            onChange={() => handleSectionExpand('outcomes')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Expected Outcomes
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{expectedOutcomes}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {evaluationPlan && (
                        <Accordion 
                            expanded={state.expandedSections.evaluation || false}
                            onChange={() => handleSectionExpand('evaluation')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Evaluation Plan
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{evaluationPlan}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {sustainabilityPlan && (
                        <Accordion 
                            expanded={state.expandedSections.sustainability || false}
                            onChange={() => handleSectionExpand('sustainability')}
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" color="primary">
                                    <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Sustainability Plan
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{sustainabilityPlan}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </CardContent>
            </Card>
            </Grid>
        )}
    </Grid>
    </Box>
);

const renderDescriptionTab = () => (
    <Box sx={{ p: 3 }}>
    <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
        title="Project Description"
        avatar={<DescriptionIcon color="primary" />}
        />
        <CardContent>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
            {projectDescription || 'No description provided'}
        </Typography>
        </CardContent>
    </Card>

    <Card variant="outlined">
        <CardHeader
        title="Research Focus"
        avatar={<TitleIcon color="primary" />}
        />
        <CardContent>
        <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            {projectTitle}
        </Typography>
        <Chip
            icon={<SchoolIcon />}
            label={getIdCategory(universityName, state.UniversitiesData)}
            color="primary"
            variant="outlined"
            sx={{ mt: 1 }}
        />
        </CardContent>
    </Card>

    {/* Objectives Section */}
    {objectives && (
        <Card variant="outlined" sx={{ mt: 3 }}>
            <CardHeader
                title="Objectives"
                avatar={<TrackChangesIcon color="primary" />}
            />
            <CardContent>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {objectives}
                </Typography>
            </CardContent>
        </Card>
    )}

    {/* Activities Section */}
    {projectActivities && (
        <Card variant="outlined" sx={{ mt: 3 }}>
            <CardHeader
                title="Activities"
                avatar={<DescriptionIcon color="primary" />}
            />
            <CardContent>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {projectActivities}
                </Typography>
            </CardContent>
        </Card>
    )}
    </Box>
);

const renderFundersTab = () => {
    const funders = projectFunders ? JSON.parse(projectFunders) : [];
    
    return (
        <Box sx={{ p: 3 }}>
            <Card variant="outlined">
                <CardHeader
                    title="Funding Partners"
                    avatar={<AttachMoneyIcon color="primary" />}
                    action={
                        <Typography variant="body2" color="text.secondary">
                            {funders.length} organizations
                        </Typography>
                    }
                />
                <CardContent>
                    <List>
                        {funders.length > 0 ? (
                            funders.map((item, index) => (
                                <ListItem key={index} sx={{ py: 2 }}>
                                    <ListItemIcon>
                                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                            <CorporateFareIcon />
                                        </Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6" fontWeight="medium">
                                                {typeof item === 'object' ? (item.data || item.name || '') : item}
                                            </Typography>
                                        }
                                        secondary="Funding Partner"
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                No funding partners listed
                            </Typography>
                        )}
                    </List>
                </CardContent>
            </Card>

            {/* Budget Section */}
            {proposedBudget && (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardHeader
                        title="Proposed Budget"
                        avatar={<AttachMoneyIcon color="primary" />}
                    />
                    <CardContent>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {proposedBudget}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

const renderMembersTab = () => {
    const members = projectMembers ? JSON.parse(projectMembers) : [];
    
    return (
        <Box sx={{ p: 3 }}>
            <Card variant="outlined">
                <CardHeader
                    title="Project Team"
                    avatar={<GroupsIcon color="primary" />}
                    action={
                        <Typography variant="body2" color="text.secondary">
                            {members.length} members
                        </Typography>
                    }
                />
                <CardContent>
                    <Grid container spacing={2}>
                        {members.length > 0 ? (
                            members.map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                                        <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
                                            <PersonIcon />
                                        </Avatar>
                                        <Typography variant="h6" gutterBottom>
                                            {typeof item === 'object' ? (item.data || item.name || '') : item}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Team Member
                                        </Typography>
                                        <Chip 
                                            label={index === 0 ? "Lead" : "Member"} 
                                            size="small" 
                                            color={index === 0 ? "primary" : "default"}
                                            sx={{ mt: 1 }}
                                        />
                                    </Card>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                    No team members listed
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {/* Outcomes Section */}
            {expectedOutcomes && (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardHeader
                        title="Expected Outcomes"
                        avatar={<TrendingUpIcon color="primary" />}
                    />
                    <CardContent>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {expectedOutcomes}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Evaluation Plan Section */}
            {evaluationPlan && (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardHeader
                        title="Evaluation Plan"
                        avatar={<AssessmentIcon color="primary" />}
                    />
                    <CardContent>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {evaluationPlan}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Sustainability Plan Section */}
            {sustainabilityPlan && (
                <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardHeader
                        title="Sustainability Plan"
                        avatar={<PublicIcon color="primary" />}
                    />
                    <CardContent>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {sustainabilityPlan}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

if (state.loading) {
    return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
    </Box>
    );
}

return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <NavigationMenu />
    <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
        {/* Header Section */}
        <Slide direction="down" in={true}>
        <Paper
            elevation={3}
            sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                {projectTitle}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                {projectDescription?.substring(0, 150)}
                {projectDescription?.length > 150 && '...'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                    label={getIdCategory(universityName, state.UniversitiesData)} 
                    variant="outlined" 
                    sx={{ color: 'white', borderColor: 'white' }} 
                />
                <Chip 
                    label={getProjectStatus().status} 
                    color={getProjectStatus().color}
                />
                {hasAdditionalDetails() && (
                    <Chip 
                        icon={<CheckCircleIcon />}
                        label="Detailed Plan Available" 
                        variant="outlined"
                        sx={{ color: 'white', borderColor: 'white' }}
                    />
                )}
                </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'row', sm: 'column' } }}>
                <Tooltip title="Bookmark">
                <IconButton onClick={handleBookmark} sx={{ color: 'white' }}>
                    <BookmarkIcon color={state.bookmarked ? "secondary" : "inherit"} />
                </IconButton>
                </Tooltip>
                <Tooltip title="Share">
                <IconButton onClick={handleShare} sx={{ color: 'white' }}>
                    <ShareIcon />
                </IconButton>
                </Tooltip>
                <Button
                    variant="contained"
                    sx={{
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                            transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                    }}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </Box>
            </Box>
        </Paper>
        </Slide>

        {/* Tabs Section */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
            value={state.activeTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
            '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 'bold'
                }
            }
            }}
        >
            <Tab label="Overview" icon={<TrendingUpIcon />} iconPosition="start" />
            <Tab label="Description" icon={<DescriptionIcon />} iconPosition="start" />
            <Tab label="Funders & Budget" icon={<AttachMoneyIcon />} iconPosition="start" />
            <Tab label="Team & Outcomes" icon={<GroupsIcon />} iconPosition="start" />
        </Tabs>
        </Paper>

        {/* Content Section */}
        <Fade in={!state.loading} timeout={500}>
        <Card elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {renderTabContent()}
        </Card>
        </Fade>
    </Container>
    </Box>
);
};

export default ProjectDetails;